import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';

const ticketTiers = [
  {
    name: 'General Pass',
    price: '$25',
    tierKey: 'general',
    color: 'teal',
    features: [
      'Access to all synchronized watch party screenings',
      'Live audience reactions and community chat',
      'Vote for the Audience Choice Award',
      '4 days of festival programming',
      'Access from anywhere in the world',
      'Join film communities and create your profile page',
    ],
  },
  {
    name: 'VIP Pass',
    price: '$50',
    tierKey: 'vip',
    color: 'orange',
    featured: true,
    features: [
      'Everything in General Pass',
      'Access to all filmmaker Q&A sessions',
      'Priority entry to breakout sessions',
      'On-demand replays during the festival — rewatch films and Q&As',
      'Exclusive behind-the-scenes content',
      'Early access to the festival schedule',
      'VIP community chat channels',
    ],
  },
  {
    name: 'All-Access Pass',
    price: '$75',
    tierKey: 'allaccess',
    color: 'gold',
    features: [
      'Everything in VIP Pass',
      '7 days of on-demand replays after the festival — all films, Q&As, and sessions',
      'Host watch parties of your favorite films after the festival',
      'Priority access in filmmaker Q&A sessions',
      'Downloadable engagement reports',
      'Private filmmaker networking sessions',
      'Name in festival credits',
    ],
  },
];

const schedule = [
  { day: 'Day 1 — Jan 21', focus: 'Opening Night: Feature Film Premiere + Director Q&A' },
  { day: 'Day 2 — Jan 22', focus: 'Short Film Block + Craft Breakout Sessions' },
  { day: 'Day 3 — Jan 23', focus: 'Documentary Showcase + Industry Panels' },
  { day: 'Day 4 — Jan 24', focus: 'Awards Ceremony + Closing Night Feature' },
];

const Tickets = () => {
  const [searchParams] = useSearchParams();
  const [purchasing, setPurchasing] = useState(null);
  const [error, setError] = useState(null);

  const isSuccess = searchParams.get('success') === 'true';
  const isCanceled = searchParams.get('canceled') === 'true';

  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) {
      localStorage.setItem('synema_ref', ref);
    }
  }, [searchParams]);

  useEffect(() => {
    if (isSuccess || isCanceled) {
      window.scrollTo(0, 0);
    }
    if (isSuccess) {
      localStorage.removeItem('synema_ref');
    }
  }, [isSuccess, isCanceled]);

  const handlePurchase = async (tierKey) => {
    setPurchasing(tierKey);
    setError(null);

    const ref = searchParams.get('ref') || localStorage.getItem('synema_ref') || null;

    try {
      const res = await fetch('/.netlify/functions/create-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tier: tierKey, ref }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Something went wrong');
      }

      window.location.href = data.url;
    } catch (err) {
      setError('Unable to start checkout. Please try again or email synemafest@gmail.com.');
      setPurchasing(null);
    }
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen pt-32 pb-20 px-6">
        <div className="max-w-xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="w-20 h-20 rounded-full bg-teal/20 border border-teal/30 flex items-center justify-center mx-auto mb-8">
              <svg className="w-10 h-10 text-teal" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="font-display text-[clamp(2.5rem,5vw,4rem)] tracking-[0.04em] leading-none text-white mb-6">
              You're in.
            </h1>
            <p className="text-[1rem] font-light leading-[1.8] text-white/[0.65] mb-8">
              Your ticket to Synema Interactive Film Festival has been confirmed. Check your email for your receipt and festival access details.
            </p>
            <p className="text-[0.85rem] text-muted">
              Questions? Email <a href="mailto:synemafest@gmail.com" className="text-teal font-medium">synemafest@gmail.com</a>
            </p>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-32 pb-20 px-6">
      <div className="max-w-[1200px] mx-auto">
        {isCanceled && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-orange/10 border border-orange/20 rounded p-4 mb-8 text-center"
          >
            <p className="text-[0.88rem] text-orange">
              Checkout was canceled. No charge was made. Pick a pass below to try again.
            </p>
          </motion.div>
        )}

        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-red-500/10 border border-red-500/20 rounded p-4 mb-8 text-center"
          >
            <p className="text-[0.88rem] text-red-400">{error}</p>
          </motion.div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="font-mono text-[0.7rem] tracking-[0.25em] uppercase text-teal mb-4">
            Get Tickets
          </div>
          <h1 className="font-display text-[clamp(2.5rem,5vw,4rem)] tracking-[0.04em] leading-none text-white mb-6">
            Your seat is everywhere.
          </h1>
          <p className="text-[1rem] font-light leading-[1.8] text-white/[0.65] max-w-[640px] mb-12">
            Join the world's first communal streaming film festival from anywhere. Pick a pass that fits how deep you want to go.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          {ticketTiers.map((tier, i) => (
            <motion.div
              key={tier.name}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 * (i + 1) }}
              className={`bg-dark2 border rounded p-8 relative overflow-hidden flex flex-col ${
                tier.featured
                  ? 'border-orange/30'
                  : 'border-white/[0.06]'
              }`}
            >
              <div
                className={`absolute top-0 left-0 right-0 h-[2px] ${
                  tier.color === 'teal'
                    ? 'bg-gradient-to-r from-teal to-transparent'
                    : tier.color === 'orange'
                    ? 'bg-gradient-to-r from-orange to-transparent'
                    : 'bg-gradient-to-r from-gold to-transparent'
                }`}
              />
              {tier.featured && (
                <div className="font-mono text-[0.6rem] tracking-[0.2em] uppercase text-orange mb-4">
                  Most Popular
                </div>
              )}
              <div className={`font-display text-4xl mb-1 ${
                tier.color === 'teal' ? 'text-teal' : tier.color === 'orange' ? 'text-orange' : 'text-gold'
              }`}>
                {tier.price}
              </div>
              <div className="font-display text-xl tracking-[0.05em] text-white mb-6">{tier.name.toUpperCase()}</div>
              <ul className="space-y-3 mb-8 flex-1">
                {tier.features.map((feature, j) => (
                  <li key={j} className="flex gap-3 items-start text-[0.85rem] font-light text-white/60">
                    <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 mt-2 ${
                      tier.color === 'teal' ? 'bg-teal' : tier.color === 'orange' ? 'bg-orange' : 'bg-gold'
                    }`} />
                    {feature}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => handlePurchase(tier.tierKey)}
                disabled={purchasing !== null}
                className={`w-full text-dark font-body text-[0.78rem] font-semibold tracking-[0.08em] uppercase py-3 px-6 rounded-sm transition-all ${
                  purchasing === tier.tierKey
                    ? 'opacity-60 cursor-wait'
                    : 'hover:opacity-90 hover:-translate-y-[1px]'
                } ${
                  tier.color === 'teal'
                    ? 'bg-teal'
                    : tier.color === 'orange'
                    ? 'btn-gradient'
                    : 'bg-gold'
                }`}
              >
                {purchasing === tier.tierKey ? 'Redirecting to checkout...' : `Get ${tier.name}`}
              </button>
            </motion.div>
          ))}
        </div>
        <p className="text-[0.72rem] text-muted text-center mb-12">
          *Stay tuned for updates on additional perks and audience prizes.
        </p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="font-mono text-[0.7rem] tracking-[0.25em] uppercase text-teal mb-4">
            Festival Schedule
          </div>
          <h2 className="font-display text-[clamp(2rem,4vw,3rem)] tracking-[0.04em] leading-none text-white mb-8">
            Four days of cinema.
          </h2>
          <div className="space-y-3">
            {schedule.map((item, i) => (
              <div
                key={i}
                className="bg-dark2 border border-white/[0.06] rounded p-5 flex items-baseline gap-6"
              >
                <span className="font-mono text-[0.75rem] tracking-[0.1em] text-teal whitespace-nowrap">
                  {item.day}
                </span>
                <span className="text-[0.9rem] font-light text-white/60">{item.focus}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Tickets;
